"use client"

import { useEffect } from "react"

const RELOAD_SESSION_KEY = "trimble-chunk-reload"

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Failed to load script/i,
]

function isChunkLoadError(message: string): boolean {
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message))
}

function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error
  if (error instanceof Error) return error.message
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message)
  }
  return ""
}

function tryRecoverFromChunkError(source: string): void {
  if (!isChunkLoadError(source)) return

  try {
    if (sessionStorage.getItem(RELOAD_SESSION_KEY)) return
    sessionStorage.setItem(RELOAD_SESSION_KEY, "1")
  } catch {
    return
  }

  window.location.reload()
}

/**
 * Recover from stale Next.js chunk requests after a deploy.
 *
 * When HTML is cached but `/_next/static/chunks/*` hashes changed, the browser
 * throws chunk load errors. A single hard reload per tab session fetches fresh
 * HTML and the matching assets.
 */
export default function ChunkLoadRecovery(): null {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      const message = event.message || getErrorMessage(event.error)
      if (isChunkLoadError(message)) {
        tryRecoverFromChunkError(message)
        return
      }

      const target = event.target
      if (
        target instanceof HTMLScriptElement &&
        target.src.includes("/_next/static/chunks/")
      ) {
        tryRecoverFromChunkError(target.src)
      }
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      tryRecoverFromChunkError(getErrorMessage(event.reason))
    }

    window.addEventListener("error", onError, true)
    window.addEventListener("unhandledrejection", onUnhandledRejection)

    return () => {
      window.removeEventListener("error", onError, true)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])

  return null
}
